<script setup lang="ts">
import { computed, ref } from 'vue';
import { Quote } from 'lucide-vue-next';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';

const props = defineProps<{
  /** Expanded HTML from page API (`pull_quote` block). */
  body?: string;
  /** Same HTML from Wagtail block-picker preview (query param `html`). */
  html?: string;
  author?: string | null;
  role?: string | null;
}>();

const quoteHtml = computed(() => (props.body ?? props.html ?? '').trim());

const quoteBodyEl = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(quoteBodyEl, () => quoteHtml.value);
</script>

<template>
  <div v-if="quoteHtml" class="pull-quote">
    <div class="pull-quote__panel">
      <div class="pull-quote__inner">
        <Quote
          class="pull-quote__deco"
          :size="24"
          :stroke-width="2"
          aria-hidden="true"
        />
        <blockquote class="pull-quote__blockquote">
          <div ref="quoteBodyEl" class="pull-quote__body" v-html="quoteHtml" />
          <footer v-if="author || role" class="pull-quote__footer">
            <cite v-if="author" class="pull-quote__author">{{ author }}</cite>
            <p v-if="role" class="pull-quote__role">{{ role }}</p>
          </footer>
        </blockquote>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pull-quote {
  position: relative;
  margin: 3rem 0;
}

@media (min-width: 640px) {
  .pull-quote {
    margin: 4rem 0;
  }
}

.pull-quote__panel {
  border-radius: 0.375rem;
  border-left: 4px solid var(--color-primary);
  padding: 2rem;
  background: linear-gradient(
    to bottom right,
    rgba(4, 120, 87, 0.08),
    rgba(4, 120, 87, 0.05),
    rgba(4, 120, 87, 0.1)
  );
}

@media (min-width: 640px) {
  .pull-quote__panel {
    padding: 2.5rem;
  }
}

@media (min-width: 768px) {
  .pull-quote__panel {
    padding: 3rem;
  }
}

.pull-quote__inner {
  position: relative;
}

.pull-quote__deco {
  position: absolute;
  top: -1rem;
  left: -0.5rem;
  width: 2.5rem;
  height: 2.5rem;
  color: var(--color-primary);
  opacity: 0.2;
  pointer-events: none;
}

@media (min-width: 640px) {
  .pull-quote__deco {
    width: 3rem;
    height: 3rem;
  }
}

.pull-quote__blockquote {
  position: relative;
  z-index: 1;
  margin: 0;
  padding-left: 1.5rem;
}

@media (min-width: 640px) {
  .pull-quote__blockquote {
    padding-left: 2rem;
  }
}

/*
 * Prototype PullQuote: p uses text-xl sm:text-2xl md:text-3xl, font-medium,
 * text-foreground, leading-relaxed. Italic is not forced here so Wagtail rich
 * text controls roman vs emphasis (<em>/<i>).
 */
.pull-quote__body {
  --pull-quote-fs: 1.25rem;
  color: var(--color-text);
  font-style: normal;
  font-size: var(--pull-quote-fs);
  font-weight: 500;
  line-height: 1.625;
}

@media (min-width: 640px) {
  .pull-quote__body {
    --pull-quote-fs: 1.5rem;
  }
}

@media (min-width: 768px) {
  .pull-quote__body {
    --pull-quote-fs: 1.875rem;
  }
}

.pull-quote__body :deep(p) {
  margin: 0;
  font-size: var(--pull-quote-fs);
  font-weight: 500;
  line-height: 1.625;
  font-style: normal;
  color: var(--color-text);
}

.pull-quote__body :deep(p + p) {
  margin-top: 0.75rem;
}

.pull-quote__body :deep(ul),
.pull-quote__body :deep(ol) {
  margin: 0.5rem 0 0;
  padding-left: 1.25em;
  font-size: var(--pull-quote-fs);
  font-weight: 500;
  line-height: 1.625;
  font-style: normal;
  color: var(--color-text);
}

.pull-quote__body :deep(li) {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}

.pull-quote__body :deep(strong) {
  font-weight: 600;
}

.pull-quote__body :deep(a) {
  color: var(--color-link, #0066cc);
}

.pull-quote__body :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.2em;
  color: var(--color-primary);
}

.pull-quote__body :deep(.indmo-richtext-icon svg) {
  width: 1em;
  height: 1em;
}

.pull-quote__body :deep(.indmo-richtext-primary) {
  color: var(--color-primary);
}

.pull-quote__footer {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(4, 120, 87, 0.2);
}

.pull-quote__author {
  display: block;
  font-style: normal;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-primary);
}

@media (min-width: 640px) {
  .pull-quote__author {
    font-size: 1.125rem;
  }
}

.pull-quote__role {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

@media (min-width: 640px) {
  .pull-quote__role {
    font-size: 1rem;
  }
}
</style>
